// Mock loyalty engine (pure functions). Client-side rules here only drive the
// prototype UI — they are NOT production enforcement. A real backend must
// verify identity, count each order once, scope campaigns and redeem atomically.
import { ORDER_STATUS } from "@/lib/orders";

export const REWARD = { LOCKED: "LOCKED", AVAILABLE: "AVAILABLE", REDEEMED: "REDEEMED" };

export function maskPhone(raw) {
  const d = String(raw ?? "").replace(/\D/g, "");
  if (d.length < 4) return "••••";
  return `${d.slice(0, 2)}•• ••• ••${d.slice(-2)}`;
}

function dayStart(iso) {
  const d = new Date(iso);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}
function dayEnd(iso) {
  const d = new Date(iso);
  d.setHours(23, 59, 59, 999);
  return d.getTime();
}

export function withinCampaignDates(campaign, at) {
  if (campaign.startDate && at < dayStart(campaign.startDate)) return false;
  if (campaign.endDate && at > dayEnd(campaign.endDate)) return false;
  return true;
}

export function inBranchScope(campaign, branchId) {
  switch (campaign.branchScope) {
    case "organization":
      return true;
    case "branch":
    case "selected":
      return (campaign.branchIds || []).includes(branchId);
    default:
      return false;
  }
}

// Is the campaign running for this branch right now?
export function isCampaignActive(campaign, { branchId, now = Date.now() }) {
  return !!campaign?.enabled && inBranchScope(campaign, branchId) && withinCampaignDates(campaign, now);
}

// The de-duplication key an order counts under, or null if it does not qualify.
// Only COMPLETED orders qualify; placed/preparing/ready/serving/canceled never do.
export function qualifyingKey(order, campaign) {
  if (!order || order.status !== ORDER_STATUS.COMPLETED) return null;
  if (!inBranchScope(campaign, order.branchId)) return null;
  if (!withinCampaignDates(campaign, order.completedAt ?? order.placedAt)) return null;
  if (campaign.qualifyingEvent === "visit") return order.sessionId ? `visit:${order.sessionId}` : null;
  return `order:${order.id}`;
}

export function createMember(phone, campaign, now = Date.now()) {
  // Data minimization: only the masked number is kept, never the full phone.
  return {
    campaignId: campaign.id,
    phoneMasked: maskPhone(phone),
    progress: 0,
    reward: REWARD.LOCKED,
    countedKeys: [],
    redemptions: [],
    joinedAt: now,
  };
}

// Credit one order. Idempotent: an order (or visit) can never count twice.
// While a reward is waiting to be redeemed, new completions are left uncounted
// (not lost) and are picked up by redeemAndSync / syncMember after redemption.
// A REDEEMED status stays REDEEMED until the next reward unlocks.
export function creditOrder(member, order, campaign) {
  if (!member || !campaign?.enabled) return member;
  if (member.campaignId !== campaign.id) return member;
  const key = qualifyingKey(order, campaign);
  if (!key || member.countedKeys.includes(key)) return member;
  if (member.reward === REWARD.AVAILABLE) return member;
  const progress = Math.min(campaign.threshold, member.progress + 1);
  let reward = REWARD.LOCKED;
  if (progress >= campaign.threshold) reward = REWARD.AVAILABLE;
  else if (member.reward === REWARD.REDEEMED) reward = REWARD.REDEEMED;
  return {
    ...member,
    progress,
    reward,
    countedKeys: [...member.countedKeys, key],
  };
}

// Credit every eligible order completed since the member joined, oldest first.
export function syncMember(member, orders, campaign) {
  if (!member) return member;
  return orders
    .filter((o) => o.status === ORDER_STATUS.COMPLETED && (o.completedAt ?? 0) >= member.joinedAt)
    .sort((a, b) => a.completedAt - b.completedAt)
    .reduce((m, o) => creditOrder(m, o, campaign), member);
}

// Join. Product behavior: a guest who joins right after their visit may get the
// most recent eligible order of the CURRENT table session credited — once.
// Orders still in progress are credited automatically when they complete.
export function joinMember(phone, orders, campaign, { sessionId, now = Date.now() } = {}) {
  let member = createMember(phone, campaign, now);
  const lastCompleted = orders
    .filter((o) => o.sessionId === sessionId && o.status === ORDER_STATUS.COMPLETED)
    .sort((a, b) => b.completedAt - a.completedAt)[0];
  if (lastCompleted) member = creditOrder(member, lastCompleted, campaign);
  return member;
}

// Redeem an AVAILABLE reward exactly once.
export function redeemReward(member, now = Date.now()) {
  if (!member || member.reward !== REWARD.AVAILABLE) return member;
  return {
    ...member,
    reward: REWARD.REDEEMED,
    progress: 0,
    redemptions: [...member.redemptions, { campaignId: member.campaignId, at: now }],
  };
}

// Redeem, then immediately credit eligible completions that were left uncounted
// while the reward was AVAILABLE — no reload or unrelated order change needed.
export function redeemAndSync(member, orders, campaign, now = Date.now()) {
  const redeemed = redeemReward(member, now);
  return redeemed === member ? member : syncMember(redeemed, orders, campaign);
}

// Load a stored member for the CURRENT campaign. Campaign-specific state
// (progress, reward, counted orders) survives only when the stored campaign id
// matches; otherwise the member keeps their masked phone and starts fresh in the
// new campaign. Earlier prototype builds kept the raw phone — it is masked here.
export function migrateMember(stored, campaign, now = Date.now()) {
  if (!stored) return null;
  const phoneMasked = stored.phoneMasked || maskPhone(stored.phone);
  if (stored.campaignId !== campaign.id) {
    const redemptions = (stored.redemptions || []).filter((r) => r?.campaignId);
    return { ...createMember("", campaign, now), phoneMasked, redemptions };
  }
  if (stored.phoneMasked) return stored;
  return {
    campaignId: campaign.id,
    phoneMasked,
    progress: stored.progress || 0,
    reward: stored.reward || REWARD.LOCKED,
    countedKeys: (stored.countedOrderIds || []).map((id) => `order:${id}`),
    redemptions: stored.redeemedAt ? [{ campaignId: campaign.id, at: stored.redeemedAt }] : [],
    joinedAt: now,
  };
}
